import {ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {SpatialDataService} from "../spatial-data.service";
import {Subscription} from "rxjs";
import {VariablesCategoriesService} from "../shared/services/variables-categories.service";
import {MatTreeFlatDataSource, MatTreeFlattener} from "@angular/material/tree";
import {FlatNode, SimpleNode} from "../shared/models/simple-node.model";
import {FlatTreeControl} from "@angular/cdk/tree";
import {MatAutocomplete} from "@angular/material/autocomplete";

@Component({
  selector: 'app-heatmap',
  templateUrl: './heatmap.component.html',
  styleUrls: ['./heatmap.component.scss'],
})
export class HeatmapComponent implements OnInit, OnDestroy {

  maxIntensity = 50;
  radius = 10;
  opacity = 0.7;

  mapOptions: google.maps.MapOptions = {
    mapTypeId: 'satellite',
    center: {
      lat: 32.805749669727064,
      lng: 35.59280158681916
    },
    zoom: 11.5,
    disableDefaultUI: true,
  };

  heatmapOptions: google.maps.visualization.HeatmapLayerOptions = {};

  heatmapData: google.maps.visualization.WeightedLocation[] = [];
  closestDate: string;
  form: FormGroup;
  @ViewChild('myForm') myForm;
  @ViewChild('auto') autocomplete: MatAutocomplete;
  @ViewChild('autoTreeInput') autoInput: ElementRef;
  spatialVariablesSubscription: Subscription;
  spatialDataSubscription: Subscription;
  variablesCategoriesSubscription: Subscription;
  formValueChangesSubscription: Subscription;
  collectionNameValueChangesSubscription: Subscription;
  flatNodeMap = new Map<FlatNode, SimpleNode>();
  nestedNodeMap = new Map<SimpleNode, FlatNode>();
  treeControl: FlatTreeControl<FlatNode>;
  treeFlattener: MatTreeFlattener<SimpleNode, FlatNode>;
  variablesCategories: MatTreeFlatDataSource<SimpleNode, FlatNode>;
  /**
   * #23 — display label of the currently selected variable. Kept separate from
   * `form.controls.column`, which must remain the technical key sent to /spatial-data.
   */
  selectedColumnTitle: string = null;
  spatialVariables: any[];
  minibatDates = [
    '27-11-2000',
    '14-12-2000',
    '26-12-2000',
    '16-01-2001',
    '18-02-2001',
    '27-03-2001',
    '05-06-2001',
    '22-07-2002',
    '28-09-2004',
    '01-02-2005',
    '14-03-2005',
    '21-04-2005',
    '18-07-2005',
    '29-08-2005',
    '16-10-2005',
    '14-11-2005',
    '13-03-2006',
    '11-05-2006',
    '08-06-2006',
    '24-01-2007',
    '28-02-2007',
    '01-05-2007',
    '26-07-2007',
    '06-09-2007',
    '10-12-2009',
    '06-01-2010',
    '24-02-2010',
    '08-04-2010',
    '10-06-2010',
    '05-08-2010',
    '04-01-2011',
    '24-08-2011',
    '06-12-2011',
    '15-02-2012',
    '03-04-2012',
    '19-08-2012',
  ];
  loading: boolean;

  colorLegend = [
    {color: '#00ff00', range: '0-10'}, // Green
    {color: '#ffff00', range: '11-20'}, // Yellow
    {color: '#ff8000', range: '21-30'}, // Orange
    {color: '#ff0000', range: '31-50'}, // Red
  ];


  constructor(
    private spatialDataService: SpatialDataService,
    private variablesCategoriesService: VariablesCategoriesService,
    private renderer: Renderer2,
    private crf: ChangeDetectorRef
  ) {
    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      this.getLevel,
      this.isExpandable,
      this.getChildren
    );
    this.treeControl = new FlatTreeControl<FlatNode>(
      this.getLevel,
      this.isExpandable
    );
    this.variablesCategories = new MatTreeFlatDataSource(
      this.treeControl,
      this.treeFlattener
    );
  }

  ngOnInit(): void {
    this.setHeatMapOptions();
    this.getSpatialVariables();
    this.setForm();
    this.variablesCategoriesService.getVariablesCategories(false, 'spatial');
    this.subscribeToVariableCategories();
    this.subscribeToFormValuesChanges();
  }

  setHeatMapOptions(): void {
    this.heatmapOptions = {
      dissipating: true,
      maxIntensity: this.maxIntensity,
      radius: this.radius,
      opacity: this.opacity,
    }

    this.updateColorLegend(this.maxIntensity);
  }

  private updateColorLegend(maxValue: number) {
    const step = maxValue / 4; // Adjust steps based on your requirement
    this.colorLegend = [
      {color: '#00ff00', range: `0-${Math.round(step)}`},
      {color: '#ffff00', range: `${Math.round(step + 1)}-${Math.round(step * 2)}`},
      {color: '#ff8000', range: `${Math.round(step * 2 + 1)}-${Math.round(step * 3)}`},
      {color: '#ff0000', range: `${Math.round(step * 3 + 1)}-${Math.round(maxValue)}`},
    ];
  }

  private subscribeToFormValuesChanges(): void {
    this.collectionNameValueChangesSubscription = this.form.get('collectionName').valueChanges.subscribe(collectionName => {
      if (collectionName === 'minibat') {
        this.form.controls.minibatDate.setValidators(Validators.required)
        this.form.controls.date.clearValidators()
        this.form.controls.date.setValue(null)
      } else {
        this.form.controls.date.setValidators(Validators.required)

        this.form.controls.minibatDate.clearValidators()

        this.form.controls.depth.setValue(0)
        this.form.controls.minibatDate.setValue(null)
      }

      this.form.controls.date.updateValueAndValidity()
      this.form.controls.minibatDate.updateValueAndValidity()
      this.form.controls.depth.updateValueAndValidity()
    })
  }

  private subscribeToVariableCategories(): void {
    this.variablesCategoriesSubscription = this.variablesCategoriesService.variablesCategoriesSubject.subscribe((data) => {
      if (data.length > 0) {
        this.variablesCategories.data = data;
      }
    });
  }

  private getSpatialVariables(): void {
    this.spatialVariablesSubscription = this.spatialDataService.getSpatialVariables().subscribe(spatialVariables => {
      this.spatialVariables = spatialVariables;
    })
  }

  private setForm(): void {
    this.form = new FormGroup({
      date: new FormControl(null),
      minibatDate: new FormControl(null),
      category: new FormControl(null, Validators.required),
      collectionName: new FormControl(null, Validators.required),
      column: new FormControl(null, Validators.required),
      depth: new FormControl(0, Validators.required),
    })
  }

  onSubmit(): void {
    this.heatmapData = [];
    if (this.form.status === 'VALID') {
      this.loading = true;
      this.spatialDataSubscription = this.spatialDataService.get(this.form.value).subscribe(spatialData => {
        if (!spatialData.message) {

          if (this.form.controls.date !== spatialData[0].date) {
            this.closestDate = spatialData[0].date;
          }

          const heatmapData = []
          spatialData.map(obj => {
            heatmapData.push(
              {location: new google.maps.LatLng(obj.latitude, obj.longitude), weight: obj.value},
            )
          })

          this.heatmapData = heatmapData;
        }
        this.loading = false;
      })
    }
  }

  private getLevel = (node: FlatNode) => node.level;

  private isExpandable = (node: FlatNode) => node.expandable;

  private getChildren = (node: SimpleNode): SimpleNode[] => node.children;

  hasChild = (_: number, _nodeData: FlatNode) => _nodeData.expandable;

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  private transformer = (node: SimpleNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.item === node.item ? existingNode : new FlatNode();
    flatNode.item = node.item;
    // #23: carry the display label alongside the technical key. `item` is what the form,
    // the selection check and the /spatial-data request use; `itemTitle` is display only.
    // Reassigned rather than set once because the reuse guard above compares `item` alone —
    // safe today since `itemTitle` is derived from `item`, but it must not go stale if that
    // ever stops being true.
    flatNode.itemTitle = node.itemTitle ? node.itemTitle : node.item;
    flatNode.level = level;
    flatNode.category = node.category ? node.category : null;
    flatNode.collectionName = node.collectionName ? node.collectionName : null;
    flatNode.level = level;
    flatNode.expandable = !!node.children;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  opened = () => {
    let inputWidth = this.autoInput.nativeElement.getBoundingClientRect().width;
    setTimeout(() => {
      let panel = (this.autocomplete.panelWidth as any).panel?.nativeElement;
      if (!panel) return;
      panel.style.minWidth = inputWidth + 'px';
    });
  };

  /** Toggle a leaf to-do item selection. Check all the parents to see if they changed */
  todoLeafItemSelectionToggle(node: FlatNode): void {
    this.form.controls.category.setValue(node.category);
    this.form.controls.collectionName.setValue(node.collectionName);
    // The form value stays the technical key: it is what /spatial-data indexes the
    // dataframe with, and what the selected-leaf check in the template compares against.
    this.form.controls.column.setValue(node.item);
    this.selectedColumnTitle = node.itemTitle ? node.itemTitle : node.item;

    // close the dropdown variables selection
    const clickEvent = this.renderer.createElement('click');
    this.renderer.appendChild(document.body, clickEvent);
    clickEvent.click();
    // Display only — the user sees the configured public name, never the technical key.
    this.autoInput.nativeElement.value = this.selectedColumnTitle;
  }

  getSelectedItems(): string {
    if (!this.form.value.column) return 'Select Variable';
    // #23: the placeholder echoes the selection, so it must show the display label. Falls
    // back to the technical key when no label was recorded.
    return this.selectedColumnTitle ? this.selectedColumnTitle : this.form.value.column;
  }

  filterChanged(e: any) {
    const filterText = e.target.value;
    this.variablesCategoriesService.filter(filterText);
    if (filterText) {
      this.treeControl.expandAll();
    } else {
      this.treeControl.collapseAll();
    }
  }

  ngOnDestroy(): void {
    if (this.spatialVariablesSubscription) {
      this.spatialVariablesSubscription.unsubscribe();
    }
    if (this.spatialDataSubscription) {
      this.spatialDataSubscription.unsubscribe();
    }
    if (this.variablesCategoriesSubscription) {
      this.variablesCategoriesSubscription.unsubscribe();
    }
    if (this.formValueChangesSubscription) {
      this.formValueChangesSubscription.unsubscribe();
    }
    if (this.collectionNameValueChangesSubscription) {
      this.collectionNameValueChangesSubscription.unsubscribe();
    }
  }

}
